import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import * as Clipboard from "expo-clipboard";
import { TierBody, TierHeadline } from "@dramadetector/ui";

import { GlassButton } from "../glass";
import { sharedStyles } from "./sharedStyles";

interface PrimaryRewriteCardProps {
	rewriteText: string;
	reasoning?: string | null;
	t: (key: string, options?: Record<string, unknown>) => string;
	showCopyAction?: boolean;
	onCopyAction?: () => void;
	testID?: string;
}

export function PrimaryRewriteCard({
	rewriteText,
	reasoning,
	t,
	showCopyAction = false,
	onCopyAction,
	testID = "primary-rewrite-card",
}: PrimaryRewriteCardProps) {
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) {
				clearTimeout(copyTimeoutRef.current);
			}
		};
	}, []);

	const handleCopyRewrite = useCallback(async () => {
		if (copyTimeoutRef.current) {
			clearTimeout(copyTimeoutRef.current);
		}

		await Clipboard.setStringAsync(rewriteText);
		onCopyAction?.();
		setCopied(true);
		copyTimeoutRef.current = setTimeout(() => {
			setCopied(false);
			copyTimeoutRef.current = null;
		}, 2000);
	}, [onCopyAction, rewriteText]);

	return (
		<View style={sharedStyles.glassCard} testID={testID}>
			<View style={sharedStyles.sectionHeader}>
				<TierHeadline size="lg" weight="semibold" color="text-gray-800" as="h3">
					{t("home.modern.best_next_version", {
						defaultValue: "Better version to send",
					})}
				</TierHeadline>
			</View>

			<View style={[sharedStyles.snippetBox, sharedStyles.afterBox, styles.rewriteBox]}>
				<TierBody size="xs" color="text-gray-500" style={styles.label}>
					{t("home.modern.try_sending", {
						defaultValue: "Try sending:",
					})}
				</TierBody>
				<TierBody size="base" color="text-gray-800" style={styles.rewriteText}>
					“{rewriteText}”
				</TierBody>
			</View>

			{reasoning && (
				<TierBody size="sm" color="text-gray-600" style={styles.reasoning}>
					{reasoning}
				</TierBody>
			)}

			{showCopyAction && (
				<View style={styles.actions} testID={`${testID}-actions`}>
					<GlassButton
						onPress={() => {
							void handleCopyRewrite();
						}}
						variant="primary"
						size="compact"
						style={styles.copyButton}
						testID={`${testID}-copy-button`}
					>
						{copied
							? t("home.modern.copy_this_version_done", {
									defaultValue: "Copied",
								})
							: t("home.modern.copy_this_version", {
									defaultValue: "Copy this version",
								})}
					</GlassButton>
				</View>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	rewriteBox: {
		marginTop: 0,
	},
	label: {
		marginBottom: 6,
		fontWeight: "600",
	},
	rewriteText: {
		lineHeight: 24,
		fontWeight: "600",
	},
	reasoning: {
		marginTop: 12,
		lineHeight: 20,
	},
	actions: {
		marginTop: 16,
		alignItems: "flex-start",
	},
	copyButton: {
		alignSelf: "flex-start",
	},
});
